import { useEffect, useState } from 'react';

/**
 * Reactive viewport breakpoint hook. Matches the AntD grid breakpoints so
 * hook results line up with `<Col xs sm md lg xl>` behavior.
 *
 * Returned booleans update on window resize / orientation change.
 */
const BP = {
  xs: 480,   // phone
  sm: 576,
  md: 768,   // tablet portrait
  lg: 992,   // laptop / where the sidebar goes from overlay → sticky
  xl: 1200,
} as const;

export function useBreakpoint(): {
  width: number;
  isMobile: boolean;    // < md (768px) — phone
  isTablet: boolean;    // md–lg range
  isDesktop: boolean;   // >= lg (992px)
} {
  const [width, setWidth] = useState<number>(() =>
    typeof window === 'undefined' ? BP.xl : window.innerWidth,
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return {
    width,
    isMobile: width < BP.md,
    isTablet: width >= BP.md && width < BP.lg,
    isDesktop: width >= BP.lg,
  };
}
